/**
 * EditUserModal
 *
 * Lightweight modal for admins to edit a user's profile fields
 * (`name`, `phone`, `gender`, `domisili`). Used as the auto-recovery surface when
 * `POST /registrations/:id/approve` returns a 409 "Incomplete User
 * Profile" — the modal opens prefilled with the user's current data and
 * highlights the fields the backend reported as missing. On save, the
 * caller can opt to immediately retry the approval.
 */
import { useMemo, useState } from 'react';
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@lms/ui';
import { Bilingual } from './Bilingual';
import { copy } from '../copy/copy';
import { useUser, useUpdateUser } from '../hooks/useUsers';
import { useLanguage } from '../lib/LanguageContext';
import { CityAutocomplete } from './CityAutocomplete';
import type { User } from '@lms/shared/types';

export type MissingProfileField = 'phone' | 'gender' | 'name';

interface EditUserModalProps {
  userId: string;
  /** Fields the backend flagged as missing, e.g. `['phone', 'gender']`. */
  missingFields?: MissingProfileField[];
  /** Optional banner message from the backend's 409 response. */
  reason?: string;
  /** Called after a successful PUT /users/:id. */
  onSaved: (updatedUser: User) => void;
  onClose: () => void;
  /**
   * Label/handler for an optional secondary CTA. Used by the
   * registrations approve flow to present a "Save & Approve" button
   * that retries the approval after the profile is updated.
   */
  primaryActionLabel?: { id: string; ar: string };
}

export function EditUserModal({
  userId,
  missingFields = [],
  reason,
  onSaved,
  onClose,
  primaryActionLabel,
}: EditUserModalProps) {
  const { t } = useLanguage();
  const { data: user, isLoading } = useUser(userId);
  const updateMutation = useUpdateUser();

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Edits state — only stores values that the user has explicitly changed.
  const [nameEdit, setNameEdit] = useState<string | null>(null);
  const [phoneEdit, setPhoneEdit] = useState<string | null>(null);
  const [genderEdit, setGenderEdit] = useState<'MALE' | 'FEMALE' | '' | null>(null);
  const [domisiliEdit, setDomisiliEdit] = useState<string | null>(null);

  const initialDomisili = useMemo(() => {
    if (!user) return '';
    const meta =
      (user as unknown as { legacyMetadata?: Record<string, unknown> })?.legacyMetadata || {};
    return (meta.domisili as string) || (meta.city as string) || '';
  }, [user]);

  const name = nameEdit ?? user?.name ?? '';
  const phone = phoneEdit ?? user?.phone ?? '';
  const gender = genderEdit ?? (user?.gender as 'MALE' | 'FEMALE' | undefined) ?? '';
  const domisili = domisiliEdit ?? initialDomisili ?? '';

  const missingSet = useMemo(() => new Set(missingFields), [missingFields]);
  const isFieldHighlighted = (field: MissingProfileField) => missingSet.has(field);

  const stillMissing: MissingProfileField[] = [];
  if (missingSet.has('phone') && !phone.trim()) stillMissing.push('phone');
  if (missingSet.has('gender') && !gender) stillMissing.push('gender');
  if (missingSet.has('name') && !name.trim()) stillMissing.push('name');
  const isBlockedByMissing = stillMissing.length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSubmitError(null);
    setValidationError(null);

    if (isBlockedByMissing) {
      setValidationError(
        t({
          id: 'Mohon lengkapi semua kolom yang ditandai wajib.',
          ar: 'يرجى إكمال جميع الحقول المطلوبة.',
        })
      );
      return;
    }

    const data: { name?: string; phone?: string; gender?: 'MALE' | 'FEMALE'; domisili?: string } =
      {};
    const trimmedName = name.trim();
    const trimmedPhone = phone.trim();
    const trimmedDomisili = domisili.trim();
    if (trimmedName && trimmedName !== user.name) data.name = trimmedName;
    if (trimmedPhone && trimmedPhone !== user.phone) data.phone = trimmedPhone;
    if (gender && gender !== user.gender) data.gender = gender;
    if (trimmedDomisili && trimmedDomisili !== initialDomisili) data.domisili = trimmedDomisili;

    if (Object.keys(data).length === 0) {
      onSaved(user);
      return;
    }

    updateMutation.mutate(
      { id: userId, data },
      {
        onSuccess: updatedUser => {
          onSaved(updatedUser);
        },
        onError: (err: unknown) => {
          const message =
            err instanceof Error
              ? err.message
              : t({
                  id: 'Gagal memperbarui profil pengguna.',
                  ar: 'فشل تحديث ملف المستخدم.',
                });
          setSubmitError(message);
        },
      }
    );
  };

  return (
    <div className="fixed inset-0 bg-on-surface/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
      <div className="bg-surface-container-lowest rounded-2xl w-full max-w-lg shadow-level4 border border-outline-variant/10 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-6 border-b border-outline-variant/10 flex justify-between items-center bg-surface-container-low">
          <div>
            <h3 className="text-xl font-headline font-bold text-on-surface m-0">
              <Bilingual {...copy.users.editUserTitle} />
            </h3>
            <p className="text-xs font-body text-on-surface-variant m-0 mt-0.5">
              <Bilingual {...copy.users.editUserSubtitle} />
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-on-surface-variant hover:text-primary rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {isLoading ? (
            <div className="py-12 flex justify-center items-center">
              <Loader2 className="animate-spin text-primary" size={24} />
            </div>
          ) : (
            <>
              {/* Optional reason banner from backend */}
              {reason && (
                <div
                  role="alert"
                  className="flex items-start gap-2 p-3 bg-warning-container text-on-warning-container rounded-lg text-xs font-body"
                >
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>{reason}</span>
                </div>
              )}

              {/* Missing fields banner */}
              {missingFields.length > 0 && (
                <div
                  role="alert"
                  className="flex items-start gap-2 p-3 bg-error-container text-on-error-container rounded-lg text-xs font-body"
                >
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>
                    <Bilingual {...copy.users.editUserMissingFieldsBanner} />
                  </span>
                </div>
              )}

              {/* Name */}
              <div>
                <label className="block text-sm font-label font-bold text-on-surface mb-1">
                  <Bilingual {...copy.users.fieldName} />
                  {isFieldHighlighted('name') && (
                    <span className="ml-2 text-[10px] font-mono font-bold text-error uppercase">
                      <Bilingual {...copy.users.requiredField} />
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  required={isFieldHighlighted('name')}
                  value={name}
                  onChange={e => setNameEdit(e.target.value)}
                  placeholder={t(copy.users.placeholderName)}
                  className={`w-full px-4 py-2 bg-surface-container-low border rounded-lg text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                    isFieldHighlighted('name')
                      ? 'border-error/60 ring-1 ring-error/20'
                      : 'border-surface-container'
                  }`}
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-label font-bold text-on-surface mb-1">
                  <Bilingual {...copy.users.fieldPhone} />
                  {isFieldHighlighted('phone') && (
                    <span className="ml-2 text-[10px] font-mono font-bold text-error uppercase">
                      <Bilingual {...copy.users.requiredField} />
                    </span>
                  )}
                </label>
                <input
                  type="tel"
                  required={isFieldHighlighted('phone')}
                  value={phone}
                  onChange={e => setPhoneEdit(e.target.value)}
                  placeholder={t(copy.users.fieldPhonePlaceholder)}
                  className={`w-full px-4 py-2 bg-surface-container-low border rounded-lg text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                    isFieldHighlighted('phone')
                      ? 'border-error/60 ring-1 ring-error/20'
                      : 'border-surface-container'
                  }`}
                />
              </div>

              {/* Gender */}
              <div>
                <label className="block text-sm font-label font-bold text-on-surface mb-1">
                  <Bilingual {...copy.users.fieldGender} />
                  {isFieldHighlighted('gender') && (
                    <span className="ml-2 text-[10px] font-mono font-bold text-error uppercase">
                      <Bilingual {...copy.users.requiredField} />
                    </span>
                  )}
                </label>
                <select
                  required={isFieldHighlighted('gender')}
                  value={gender}
                  onChange={e => setGenderEdit(e.target.value as 'MALE' | 'FEMALE' | '')}
                  className={`w-full px-4 py-2 bg-surface-container-low border rounded-lg text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                    isFieldHighlighted('gender')
                      ? 'border-error/60 ring-1 ring-error/20'
                      : 'border-surface-container'
                  }`}
                >
                  <option value="">—</option>
                  <option value="MALE">{t(copy.users.genderMale)}</option>
                  <option value="FEMALE">{t(copy.users.genderFemale)}</option>
                </select>
              </div>

              {/* Domisili / Kota Kabupaten with Auto-suggest */}
              <div>
                <label className="block text-sm font-label font-bold text-on-surface mb-1">
                  <Bilingual {...copy.users.labelDomisili} />
                </label>
                <CityAutocomplete
                  value={domisili}
                  onChange={val => setDomisiliEdit(val)}
                  placeholder="Ketik nama kota/kabupaten domisili..."
                />
              </div>

              {/* Inline validation feedback */}
              {validationError && (
                <div
                  role="alert"
                  className="flex items-start gap-2 p-3 bg-warning-container text-on-warning-container rounded-lg text-xs font-body"
                >
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>{validationError}</span>
                </div>
              )}

              {/* Inline error from update mutation */}
              {submitError && (
                <div
                  role="alert"
                  className="flex items-start gap-2 p-3 bg-error-container text-on-error-container rounded-lg text-xs font-body"
                >
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>{submitError}</span>
                </div>
              )}

              <div className="pt-4 border-t border-surface-container flex justify-end gap-3">
                <Button type="button" variant="ghost" onClick={onClose}>
                  <Bilingual {...copy.users.btnCancel} />
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={updateMutation.isPending || isBlockedByMissing}
                >
                  {updateMutation.isPending ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Bilingual {...(primaryActionLabel ?? copy.users.btnSaveOnly)} />
                  )}
                </Button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
